import { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { type QueryClient } from '@tanstack/react-query';

import { useDeleteAuditByIdMutation } from '#apps/client-web/features/audit/hooks/use-delete-audit-by-id-mutation';
import { useLoadAuditByIdQuery } from '#apps/client-web/features/audit/hooks/use-load-audit-by-id-query';
import { type AuditService } from '#apps/client-web/features/audit/services/audit';
import { AUDITS_QUERY_KEY } from '#apps/client-web/features/audit/types/react-query';
import { useNavigateBack } from '#apps/client-web/app/hooks/use-navigate-back';
import { type AuditFormService } from '#apps/client-web/features/audit-form/services/audit-form';
import { type ExportApiService } from '#apps/client-web/features/export/services/export-api';
import {
  useExportTemplateMutation,
  type ExportProgress,
} from '#apps/client-web/features/export/hooks/use-export-template-mutation';
import { useAuth } from '#apps/client-web/features/auth/hooks/use-auth';

import { type Clock } from '#packages/data-collection/services/clock';
import { type LoggingContext } from '#packages/data-collection/types/logging-context';
import {
  type Audit,
  type AuditId,
} from '#packages/data-collection/models/audit';
import { type QueryPageState } from '#packages/data-collection/types/page-state';
import { useMenu } from '#packages/web/hooks/use-menu';
import { useDialog } from '#packages/web/hooks/use-dialog';

export type AuditDetailsPageViewModel = ReturnType<
  typeof useAuditDetailsPageViewModel
>;

type AuditDetailsPageViewModelProps = LoggingContext & {
  auditService: AuditService;
  auditFormService: AuditFormService;
  exportApiService: ExportApiService;
  queryClient: QueryClient;
  clock: Clock;
};

export const useAuditDetailsPageViewModel = ({
  auditService,
  auditFormService,
  exportApiService,
  queryClient,

  logger,
}: AuditDetailsPageViewModelProps) => {
  const { auditId } = useParams<{ auditId: AuditId }>();
  const goBack = useNavigateBack({ logger });
  const { user } = useAuth({ logger });

  const auditDetailsQuery = useLoadAuditByIdQuery({
    auditService,
    queryClient,
    collectionName: 'active',
    auditId: auditId!,
  });

  const pageState = useMemo<QueryPageState<Audit>>(() => {
    if (auditDetailsQuery.isSuccess) {
      return { state: 'success', data: auditDetailsQuery.data };
    }

    if (auditDetailsQuery.isLoading || auditDetailsQuery.isPending) {
      return { state: 'loading' };
    }

    if (auditDetailsQuery.isError) {
      return { state: 'error', error: auditDetailsQuery.error };
    }

    throw new Error('Unhandled query state');
  }, [
    auditDetailsQuery.isSuccess,
    auditDetailsQuery.isLoading,
    auditDetailsQuery.isPending,
    auditDetailsQuery.isError,
    auditDetailsQuery.error,
  ]);

  const mainMenu = useMenu();

  const exportTemplateMutation = useExportTemplateMutation({
    exportApiService,
    auditFormService,
    queryClient,
    getAccessToken: () => user?.access_token,
    logger,
  });

  const [exportProgress, setExportProgress] = useState<ExportProgress | null>(
    null,
  );

  const onExport = useCallback(() => {
    if (pageState.state !== 'success') {
      logger?.debug('Cannot export, page is not in success state');
      return;
    }

    mainMenu.close();
    logger?.debug('Export requested');
    setExportProgress({ percent: 0, label: 'Starting export…' });
    exportTemplateMutation.mutate(
      {
        audit: pageState.data,
        onProgress: (progress) => setExportProgress(progress),
      },
      {
        onSuccess: () => {
          logger?.debug('Export download triggered');
        },
        onError: (err) => {
          logger?.error({ err }, 'Export download failed');
          setExportProgress(null);
        },
      },
    );
  }, [exportTemplateMutation.mutate, pageState]);

  const dismissExportProgress = useCallback(() => {
    setExportProgress(null);
  }, []);

  const deleteDialog = useDialog<Audit>();
  const deleteAuditByIdMutation = useDeleteAuditByIdMutation({
    auditService,
    queryClient,
  });
  const onDelete = useCallback(
    ({ confirmed }: { confirmed: boolean }) => {
      if (pageState.state !== 'success') {
        logger?.debug('Cannot delete audit, page is not in success state');
        return;
      }

      mainMenu.close();

      if (!confirmed) {
        logger?.debug('Opening audit deletion confirmation dialog');
        deleteDialog.open(pageState.data);
        return;
      }

      deleteAuditByIdMutation.mutate(
        {
          auditId: auditId!,
          collectionName: 'active',
        },
        {
          onSuccess: (audit) => {
            logger?.debug({ auditId, audit }, 'Audit deleted successfully');

            queryClient
              .invalidateQueries({
                queryKey: [AUDITS_QUERY_KEY],
              })
              .then(() => {
                logger?.debug('Audit list queries invalidated');
                goBack();
              })
              .catch((err) => {
                logger?.error(
                  { err },
                  'Failed to invalidate audit list queries',
                );
              });
          },

          onError: (err) => {
            logger?.error({ err, auditId }, 'Failed to delete audit');
            throw err;
          },
        },
      );
    },
    [auditId, pageState],
  );

  return {
    mainMenu,

    auditDetailsQuery,

    auditId,
    page: pageState,

    onExport,
    exportProgress,
    isExportPending: exportTemplateMutation.isPending,
    dismissExportProgress,

    onDelete,
    deleteDialog,
  };
};
